import { Component, EventEmitter, OnInit, Output, SecurityContext, ChangeDetectionStrategy, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { ContactFormApiService } from '@app/common/services/contact-form.api.service';
import { ContactFormControls, ContactFormData, ContactFormFile } from '@app/types/contact-form.types';
import { DomSanitizer } from '@angular/platform-browser';
import { FilesInputComponent } from '@app/common/components/files-input/files-input.component';
import { FurnitureType } from '@app/types/furniture.types';
import { LoggerService } from '@app/common/services/logger.service';
import { LoggerType } from '@app/types/logger.types';
import { MpTooltipDirective } from '@app/common/directives/mp-tooltip.directive';
import { MpTooltipPosition } from '@app/types/mp-tooltip.types';

@Component({
  selector: 'app-contact-form',
  imports: [MpTooltipDirective, ReactiveFormsModule, FilesInputComponent],
  templateUrl: './contact-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './contact-form.component.scss'
})
export class ContactFormComponent implements OnInit {
  private contactFormApiService = inject(ContactFormApiService);
  private domSanitizer = inject(DomSanitizer);
  private formBuilder = inject(FormBuilder);
  private loggerService = inject(LoggerService);

  @Output() formSubmitted = new EventEmitter<void>();

  contactForm: FormGroup;
  submitting = false;

  readonly ContactFormControls = ContactFormControls;
  readonly FurnitureType = FurnitureType;
  readonly MpTooltipPosition = MpTooltipPosition;

  private filesToUpload: ContactFormFile[];

  ngOnInit(): void {
    this.createContactForm();
  }

  validateFormControl(controlName: ContactFormControls): void {
    this.contactForm.get(controlName).markAsTouched({ onlySelf: true });
    this.contactForm.get(controlName).updateValueAndValidity({ onlySelf: true });
  }

  addFiles(files: ContactFormFile[]): void {
    this.filesToUpload = files;
  }

  submitForm(): void {
    this.submitting = true;

    const sanitizedFormValue: ContactFormData = {};
    Object.keys(this.contactForm.value).forEach((k) => {
      sanitizedFormValue[k] = this.domSanitizer.sanitize(SecurityContext.HTML, this.contactForm.value[k]);
    });

    this.contactFormApiService.sendMessage({ ...sanitizedFormValue, files: this.filesToUpload }).subscribe({
      next: () => {
        this.loggerService.log(LoggerType.Success, 'Dziękujemy za kontakt!', 'Zapytanie zostało wysłane.');
        this.contactForm.reset();
        this.filesToUpload = [];
        this.submitting = false;
        this.formSubmitted.emit();
      },
      error: () => (this.submitting = false)
    });
  }

  private createContactForm(): void {
    this.contactForm = this.formBuilder.group({
      [ContactFormControls.Name]: [null, Validators.required],
      [ContactFormControls.Contact]: [null, [this.contactValidator(), Validators.required]],
      [ContactFormControls.FurnitureType]: [null, [Validators.required, this.valueSelectedValidator()]],
      [ContactFormControls.Location]: [null, Validators.required],
      [ContactFormControls.Description]: [null, Validators.required]
    });
  }

  private valueSelectedValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value !== 'null' ? null : { valueSelected: false };
    };
  }

  private contactValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const emailPattern = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, 'i');
      const emailValidationResult = emailPattern.test(control.value) ? null : { email: false };
      const phonePattern = new RegExp(/^(\+48)?[ -]?(\d{9}|\d{3}[ -]?\d{3}[ -]?\d{3}|\d{2}[ -]?\d{3}[ -]?\d{2}[ -]?\d{2})$/);
      const phoneValidationResult = phonePattern.test(control.value) ? null : { phone: false };

      if (!emailValidationResult || !phoneValidationResult) {
        return null;
      }
      return { contact: true };
    };
  }
}
